/* eslint-disable class-methods-use-this */
import { AxiosPromise, AxiosResponse, ResponseType } from 'axios';
import { http } from 'SharedDir/backend/axios/http';

import { integrationsUrl } from 'SharedDir/backend/endpoints/administration-integration';
import { Integrations } from 'SharedDir/declarations/integrations';
import { Paginated, PaginationParams } from 'SharedDir/declarations/pagination';
import { Section } from 'SharedDir/declarations/section-field';
import { QuasarTableExportColumns } from 'SharedDir/declarations/shared';

import { SimpleApiInterface } from './simpleApi';

import IntegrationConfig = Integrations.IntegrationConfig;

export default class IntegrationPersonConfigApiClass
implements
    SimpleApiInterface<
      Integrations.IntegrationConfigFullPerson,
      Integrations.IntegrationConfigCreatePerson,
      Integrations.IntegrationConfigFullPerson,
      Integrations.IntegrationConfigUpdatePerson
    > {
  protected readonly callHistoryUlr = '/integration/persons/histories';

  protected makeUrl(integrationId: number | string, configId?: number | string) {
    const url = `${integrationsUrl}/${integrationId}/persons/configs`;
    if (!configId) {
      return url;
    }

    return `${url}/${configId}`;
  }

  async getPaginated<Extra extends object>(params: PaginationParams<Extra>) {
    const url = `${integrationsUrl}/persons/configs`;
    const { data } = (await http({
      url,
      method: 'get',
      params,
    })) as AxiosResponse<IntegrationConfig>;
    return data;
  }

  async getAll<Extra extends object>(
    params: Omit<PaginationParams<object>, 'rowsPerPage' | 'page'> & Extra
  ) {
    const { data } = await this.getPaginated({ ...params, rowsPerPage: 0, page: 1 });
    return data;
  }

  async get(integrationId: string | number, configId: string | number) {
    const url = this.makeUrl(integrationId, configId);
    const { data: response } = await http({ method: 'get', url });
    return response;
  }

  async store(integrationId: number, data: Integrations.IntegrationConfigCreatePerson) {
    const url = this.makeUrl(integrationId);
    const { data: response } = await http({ method: 'post', url, data });
    return response;
  }

  async update(
    integrationId: string | number,
    configId: string | number,
    data: Integrations.IntegrationConfigUpdatePerson
  ) {
    const url = this.makeUrl(integrationId, configId);
    const { data: response } = await http({ method: 'put', url, data });
    return response;
  }

  async delete(integrationId: string | number, configId: string | number) {
    const url = this.makeUrl(integrationId, configId);
    const { data: response } = await http({ method: 'delete', url });
    return response;
  }

  exportTable<Extra extends object>(
    params: PaginationParams<Extra>,
    data: QuasarTableExportColumns,
    responseType?: ResponseType | undefined
  ): AxiosPromise {
    throw new Error('Method not implemented.');
  }

  exportTableAll<Extra extends object>(
    params: Omit<PaginationParams<object>, 'rowsPerPage' | 'page'> & Extra,
    data: QuasarTableExportColumns,
    responseType: ResponseType
  ): AxiosPromise {
    throw new Error('Method not implemented.');
  }

  async getPersonSections() {
    const {
      data: { data },
    } = (await http({
      url: `${integrationsUrl}/persons/sections`,
      method: 'get',
    })) as AxiosResponse<Paginated<Section>>;
    return data;
  }

  async bindPersonField<T extends Integrations.IntegrationConnectionFieldCreatePerson>(
    integrationId: string | number,
    configId: string | number,
    data: T
  ) {
    const url = `${this.makeUrl(integrationId, configId)}/bind`;
    const { data: response } = (await http({
      url,
      method: 'post',
      data,
    })) as AxiosResponse<Integrations.IntegrationConnectionFieldBarePerson>;
    return response;
  }

  async unbindPersonField(
    integrationId: string | number,
    configId: string | number,
    connectionId: number
  ) {
    const url = `${this.makeUrl(integrationId, configId)}/bind/${connectionId}`;
    const { data: response } = (await http({
      url,
      method: 'delete',
    })) as AxiosResponse<Integrations.IntegrationConnectionFieldBarePerson>;
    return response;
  }

  async getIntegrationCallsHistory() {
    const { data } = (await http({ url: this.callHistoryUlr, method: 'GET' })) as AxiosResponse<
      Paginated<Integrations.PersonIntegrationsResultBare>
    >;
    return data;
  }

  async getIntegrationCallResult(id: number) {
    const url: string = `${this.callHistoryUlr}/id`;
    const { data } = (await http({
      url,
      method: 'GET',
    })) as AxiosResponse<Integrations.PersonIntegrationsResultBare>;
    return data;
  }
}
